import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ItemWrapper } from '../../../../../components';
import { selectScheduleConstraints } from '../../../../../features/schedule/scheduleSelectors';
import { selectTournament } from '../../../../../features/tournament/tournamentSelectors';

import './TournamentCardHeadSection.scss';
import { selectCurrentClass } from '../../../../../features/class/classSelectors';
import { useAppSelector } from '../../../../../hooks/redux';
import { getTournamentTimezone } from '../../../Schedule/components/schedule.helpers';
import { formatVenueDateWithTime, resolveMatchDisplayIso } from '../../../../../utils/tournamentDateTime';

interface TournamentCardHeadSectionProps {
    matchId: string;
    court?: string;
    startDate?: string;
    scheduleDate?: string | null;
}

export const TournamentCardHeadSection: FC<TournamentCardHeadSectionProps> = ({
    matchId,
    court = '',
    startDate = '',
    scheduleDate,
}) => {
    const prefix = 'printCardHead-';

    const { t } = useTranslation();
    const { data } = useSelector(selectTournament) || {} as any;
    const { name: nameOfClass } = useAppSelector(selectCurrentClass) || { name: '' };
    const scheduleConstraints = useAppSelector(selectScheduleConstraints);

    const venueTimeZone = useMemo(() => {
        const scheduleTimezone =
            scheduleConstraints?.scheduleTimezone ??
            (data as { scheduleConstraints?: { scheduleTimezone?: string } })?.scheduleConstraints
                ?.scheduleTimezone;
        return getTournamentTimezone(data?.country, data?.timeZone, scheduleTimezone);
    }, [data?.country, data?.timeZone, scheduleConstraints?.scheduleTimezone]);

    const formatedDate = useMemo(() => {
        const iso = resolveMatchDisplayIso({ scheduleDate, startDate }, venueTimeZone);
        if (!iso) return '';
        return formatVenueDateWithTime(iso, venueTimeZone);
    }, [scheduleDate, startDate, venueTimeZone]);

    return (<>
        <div className="tournament-card-head-section">
            <div className="tournament-card-head-section__logo-name">
                <div className="tournament-card-head-section__logo-holder">
                    <img src={data?.icon || require('../../../../../assets/images/noImage.png')} alt="tournament logo" />
                </div>
                <div className='tournament-card-head-section__name'>
                    {data?.name}
                </div>
            </div>
            <div className="tournament-card-head-section__info">
                <div className="tournament-card-head-section__court">
                    <div className="tournament-card-head-section__court-title">{t(prefix + 'court')}</div>
                    <ItemWrapper className="tournament-card-head-section__information-text-box">{court}</ItemWrapper>
                </div>
                <div className="tournament-card-head-section__match-id">
                    <div className="tournament-card-head-section__match-id-title">{t(prefix + 'matchId')}</div>
                    <ItemWrapper className="tournament-card-head-section__information-text-box">{matchId}</ItemWrapper>
                </div>
                <div className="tournament-card-head-section__start-date">
                    <div className="tournament-card-head-section__start-date-title">{t(prefix + 'startDate')}</div>
                    <ItemWrapper className="tournament-card-head-section__information-text-box">{formatedDate}</ItemWrapper>
                </div>
            </div>
        </div>
        <div className="tournament-card-head-section">
            <div className="tournament-card-head-section__event">
                <div>{t(prefix + 'event')}</div>
                <ItemWrapper className="tournament-card-head-section__information-text-box">
                    {nameOfClass}
                </ItemWrapper>
            </div>
            <div>
                <div className="tournament-card-head-section__format-title">
                    {t(prefix + 'format')}
                </div>
                <div className="tournament-card-head-section__format-holder">
                    <ItemWrapper className="tournament-card-head-section__information-text-box">
                        {t(prefix + 'formatType1')}
                    </ItemWrapper>
                    <ItemWrapper className="tournament-card-head-section__information-text-box">
                        {t(prefix + 'formatType2')}
                    </ItemWrapper>
                    <ItemWrapper className="tournament-card-head-section__information-text-box">
                        {t(prefix + 'formatType3')}
                    </ItemWrapper>
                    <ItemWrapper className="tournament-card-head-section__information-text-box">
                        {t(prefix + 'formatType4')}
                    </ItemWrapper>
                </div>
            </div>
        </div>
    </>
    )
};
